import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

import AlertSettingsSection from '../components/settings/AlertSettingsSection';
import ClientAliasesSection from '../components/settings/ClientAliasesSection';
import HuntressOnlySection from '../components/settings/HuntressOnlySection';
import PlatformRequirementsSection from '../components/settings/PlatformRequirementsSection';
import ExpectedGapsSection from '../components/settings/ExpectedGapsSection';
import RunAuditSection from '../components/settings/RunAuditSection';

/**
 * Tab definition for the Settings sidebar.
 */
interface SettingsTab {
  id: string;
  label: string;
}

const SETTINGS_TABS: SettingsTab[] = [
  { id: 'alerts', label: 'Alert Settings' },
  { id: 'client-aliases', label: 'Client Aliases' },
  { id: 'huntress-only', label: 'Huntress-Only' },
  { id: 'platform-reqs', label: 'Platform Requirements' },
  { id: 'expected-gaps', label: 'Expected Gaps' },
  { id: 'run-audit', label: 'Run Audit' },
];

/**
 * Settings page with vertical sidebar navigation.
 *
 * Active tab is driven by the ?tab= URL search param to support
 * direct linking and browser back/forward navigation.
 * Uses replace: true to avoid polluting history stack.
 */
export default function SettingsPage() {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? 'alerts';

  function handleTabChange(tabId: string) {
    setSearchParams({ tab: tabId }, { replace: true });
  }

  function renderContent() {
    switch (activeTab) {
      case 'alerts':
        return <AlertSettingsSection />;
      case 'client-aliases':
        return <ClientAliasesSection />;
      case 'huntress-only':
        return <HuntressOnlySection />;
      case 'platform-reqs':
        return <PlatformRequirementsSection />;
      case 'expected-gaps':
        return <ExpectedGapsSection />;
      case 'run-audit':
        return <RunAuditSection />;
      default:
        return <AlertSettingsSection />;
    }
  }

  return (
    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
      {/* Sidebar */}
      <Box
        sx={{
          width: 200,
          flexShrink: 0,
          borderRight: `1px solid ${theme.palette.divider}`,
          pr: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontSize: '0.95rem', fontWeight: 600, mb: 1.5, px: 1 }}>
          Settings
        </Typography>
        <List disablePadding>
          {SETTINGS_TABS.map((tab) => {
            const isSelected = activeTab === tab.id;
            return (
              <ListItemButton
                key={tab.id}
                selected={isSelected}
                onClick={() => handleTabChange(tab.id)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  ...(isSelected && {
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.18),
                    },
                  }),
                }}
              >
                <ListItemText
                  primary={tab.label}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: isSelected ? 600 : 400,
                    color: isSelected ? theme.palette.primary.main : 'inherit',
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      {/* Content area */}
      <Box sx={{ flex: 1, minWidth: 0 }}>{renderContent()}</Box>
    </Box>
  );
}
